import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { ChannelCardStrip, ChannelCardStripProps } from '@/ui-components/molecules/ChannelCardStrip/ChannelCardStrip';
import { getChannelImageLoadingProps, hiddenChannelImageLoadingProps } from '../../ChannelGuide/utils';

interface PodcastStripGroupProps {
    podcasts: Array<SitePodcast>;
    fallbackImageUrl: string;
}
// helpers - move to Channels folder with Podcast Guide work
function normalizeSeconds(val?: number): number | undefined {
    if (val === undefined || val === null) return undefined;
    return val > 100_000 ? Math.floor(val / 1000) : val;
}

function formatDurationShort(input?: number): string {
    const seconds = normalizeSeconds(input);
    if (seconds === undefined) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h >= 1) return `${h}H ${m}M`;
    return `${m}M`;
}

function formatAirDateShort(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const mon = d.toLocaleString('en-US', { month: 'short' }).toUpperCase(); // JUL
    return `${mon} ${d.getDate()}`;
}

export default function PodcastStripGroup({ fallbackImageUrl, podcasts }: PodcastStripGroupProps) {
    const items: ChannelCardStripProps[] = podcasts.map((podcast, index) => {
        const title = podcast.fields.title;
        const episode = podcast.fields.episode;
        const hasEpisode = Boolean(episode && episode.episode_id);

        const showPageUrl = podcast.fields.link?.fields?.href || '';
        const episodePageUrl = hasEpisode ? episode?.link?.fields?.href : showPageUrl;

        const subTitleDetails =
            typeof podcast.fields.short_description === 'string' ? (
                podcast.fields.short_description
            ) : podcast.fields.short_description ? (
                <RenderRTE contentfulDoc={podcast.fields.short_description} removeSpanTag />
            ) : undefined;

        const date = hasEpisode ? formatAirDateShort(episode?.originalAirTime) : '';
        const dur = hasEpisode ? formatDurationShort(episode?.duration) : '';
        const latestMeta = [date, dur].filter(Boolean).join(' · '); // JUL 22 · 26M

        const podcastImgSrc = podcast.fields.web_2_0_image?.url || fallbackImageUrl;
        const podcastImgAlt = podcast.fields.web_2_0_image?.altText || `${title} artwork`;

        const episodeImgSrc = hasEpisode ? episode?.image?.url || podcastImgSrc : podcastImgSrc; // fallback to podcast image
        const episodeImgAlt = ''; // Image for Episode information is decorative and should be hidden from screen readers

        return {
            id: episode?.episode_id || podcast.fields.show_id || title,
            title,
            subTitle: undefined,
            subTitleDetails: subTitleDetails,
            live: hasEpisode,
            onAirTime: hasEpisode ? latestMeta : '',
            showName: hasEpisode ? episode?.name : title,
            onAirImage: episodeImgSrc ? (
                <ImageWrapper
                    src={episodeImgSrc}
                    alt={episodeImgAlt}
                    optimizedHeight={150}
                    optimizedWidth={150}
                    {...getChannelImageLoadingProps(index)}
                />
            ) : undefined,
            channelImage: podcastImgSrc ? (
                <ImageWrapper
                    src={podcastImgSrc}
                    alt={podcastImgAlt}
                    optimizedHeight={200}
                    optimizedWidth={200}
                    {...hiddenChannelImageLoadingProps}
                />
            ) : undefined,
            playIcon: true,
            // Links
            titleLink: showPageUrl,
            titleAriaLabel: `Start listening to ${title}`,
            listenLink: episodePageUrl || showPageUrl || '',
            listenLinkAriaLabel: `Start listening to ${title}`,
            onAirNowArialLabel: hasEpisode ? `Listen to latest episode ${episode?.name}` : `Listen to ${title}`,
            showAndScheduleLink: '',
            showsAndScheduleLinkAriaLabel: '',
            showAndScheduleLinkText: '',
            // Badges
            isAppOnlyBadge: false,
            isXLBadge: false,
            isOnlyOnBadge: false,
            locked: false,
            onAirBadgeText: hasEpisode ? 'LATEST EPISODE' : undefined,
            onAirShowIcon: false,
        };
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--generic-unit-12, 12px)' }}>
            {items.map((item) => (
                <ChannelCardStrip key={item.id} {...item} />
            ))}
        </div>
    );
}
